import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { grey } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";

import fpResProdPlanService from "@plannel/services/fp/fp-res-prod-plan-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, {
  readHide, createSumAwareFormatter, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — PlanResult 파일럿에서 확인된 값(9)을 그대로
// 재사용한다(테넌트 단일값, fp-report-screen-guide.md §A-3c).
// TODO(MIG-T2): [resprodplan/ResProdPlan] 조회조건 세팅 3종(버킷 목록/검색필드 표시/검색구간)의
//   테넌트별 커스터마이징 미이관.
//   old: search-option/bucket · search-option/srh-options · setting FP_SRH_RANGE를 각각 DB에서 조회
//   사유: 정적 구성(JSON 카탈로그/고정 카탈로그/상수)으로 전환 — 가이드 §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰는 필드 4개(loadData/두 차트 로더의 getValues 기준,
// ResProdPlanQueryRepository가 4개 모두 실제로 contains 조건으로 소비함 — InoutStatus와 같은
// "old에서도 실제로 동작하던" 케이스).
const SEARCH_FIELD_LABELS = {
  stageCdNm: "FP_STAGE", resourceCdNm: "FP_RESOURCE", routeCdNm: "FP_ROUTE", itemCdNm: "FP_ITEM"
};
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";
const SUM_ROW = "SUM";

// 백엔드가 늘 이 고정 analysisIndicator만 내려준다(ResProdPlanService) — 클릭 레벨에 따라
// 두 차트 엔드포인트/지표 세트가 갈린다(old updateChartFromGrid의 fieldName 분기와 동일):
//  - 자원(resourceCd/resourceNm) 클릭 → res-chart, 자원 가동 지표 5종(듀얼 y축)
//  - 그 외(공장/스테이지/공정/품목/피벗 셀) 클릭 → item-chart, 품목 수급 지표 3종(단일 y축)
const CHART_MODE_RESOURCE = "resource";
const CHART_MODE_ITEM = "item";

const ITEM_INDICATOR_ORDER = ["QTY", "DMND_QTY", "EOH"];
const ITEM_INDICATOR_TYPES = { QTY: "bar", DMND_QTY: "line", EOH: "line" };
const ITEM_INDICATOR_COLORS = { QTY: "#7BB7FA", DMND_QTY: "#FC5F67", EOH: "#BAF721" };

const RESOURCE_INDICATOR_ORDER = ["USED_TIME", "AVAIL_TIME", "JC_TIME", "USED_RATE", "AVAIL_RATE"];
const RESOURCE_INDICATOR_TYPES = {
  USED_TIME: "bar", AVAIL_TIME: "bar", JC_TIME: "bar", USED_RATE: "line", AVAIL_RATE: "line"
};
const RESOURCE_INDICATOR_COLORS = {
  USED_TIME: "#7BB7FA", AVAIL_TIME: "#E9226D", JC_TIME: "#000000", USED_RATE: "#6DE36F", AVAIL_RATE: "#BAF721"
};
// old customTitle — 시간 지표(bar)는 좌축 "시간(분)", 비율 지표(line)는 우축 "%". groupName이
// 다르면 PivotChartComposed가 자동으로 듀얼 y축을 배치한다(fp-report-screen-guide.md §A-7).
const RESOURCE_INDICATOR_IS_RATE = new Set(["USED_RATE", "AVAIL_RATE"]);

// 트리(행 그룹) 계층 — 공장 > 스테이지 > 자원 > 공정 > (leaf)품목. old 컬럼정의(plantGroup/
// stageGroup/resourceGroup/routeGroup 전부 `mergeRule`)를 근거로 Row Grouping을 선택했다
// (Tree Data가 아님, fp-report-screen-guide.md §A-6b). old의 그레인(그룹 기준)이
// (plant,stage,resource,route,item) 5개 전체라 route까지는 그룹 레벨이고 item이 leaf다.
const GROUP_FIELDS = [
  "plantCd", "plantNm", "stageCd", "stageNm", "resourceCd", "resourceNm", "routeCd", "routeNm"
];

const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const STAGE_CD_COL_ID = "stageCdGrp";
const STAGE_NM_COL_ID = `STAGE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const RESOURCE_CD_COL_ID = "resourceCdGrp";
const RESOURCE_NM_COL_ID = `RESOURCE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ROUTE_CD_COL_ID = "routeCdGrp";
const ROUTE_NM_COL_ID = `ROUTE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ITEM_NM_COL_ID = `ITEM_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;

const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const STAGE_NM_HIDE_KEY = "stageNmColsHide";
const RESOURCE_NM_HIDE_KEY = "resourceNmColsHide";
const ROUTE_NM_HIDE_KEY = "routeNmColsHide";
const ITEM_NM_HIDE_KEY = "itemNmColsHide";

const ResProdPlan = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // stageCdNm/resourceCdNm/routeCdNm/itemCdNm — Report 공통 정책(§A-9b)대로 조회 버튼이 없다.
  const stageCdNmRef = useRef("");
  const resourceCdNmRef = useRef("");
  const routeCdNmRef = useRef("");
  const itemCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartMode, setChartMode] = useState(CHART_MODE_RESOURCE);
  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveResProdPlan(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      getRowStyle: (params) => (params.data?.itemCd === SUM_ROW ? { backgroundColor: grey[100] } : null),
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sumAwareFormatter = createSumAwareFormatter(SUM_ROW, t);

  // old styleCallback — 자원이 과부하(resourceUtil) 상태면 자원 코드/명칭 셀 배경을 강조한다.
  const resourceOverloadCellStyle = (params) => (params.data?.resourceUtil ? { backgroundColor: "#f5ab00" } : null);

  // 고정(왼쪽 pinned) 컬럼 — old fixColumns(plantCd/Nm, stageCd/Nm, resourceCd/Nm, routeCd/Nm,
  // resourceUtil(hidden), itemCd/Nm, itemClassCd(hidden), sum)와 1:1. 공장/스테이지/자원/공정은
  // 행 그룹(트리), 품목은 leaf.
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),
    // resourceUtil 자체는 그룹 기준이 아니라 자원 코드/명칭 셀 강조용 데이터 — 그룹 없이 그대로 보관.
    { headerName: "resourceUtil", field: "resourceUtil", colId: "resourceUtil", hide: true },
    { headerName: "itemClassCd", field: "itemClassCd", colId: "itemClassCd", hide: true },

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, STAGE_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: STAGE_CD_COL_ID, cdField: "stageCd", cdHeader: "FP_STAGE_CD",
      nmColId: STAGE_NM_COL_ID, nmField: "stageNm", nmHeader: "FP_STAGE_NM",
      hideKey: STAGE_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, RESOURCE_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: RESOURCE_CD_COL_ID, cdField: "resourceCd", cdHeader: "FP_RESOURCE_CD",
      nmColId: RESOURCE_NM_COL_ID, nmField: "resourceNm", nmHeader: "FP_RESOURCE_NM",
      hideKey: RESOURCE_NM_HIDE_KEY, width: 150, cellStyle: resourceOverloadCellStyle
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ROUTE_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: ROUTE_CD_COL_ID, cdField: "routeCd", cdHeader: "FP_ROUTE_CD",
      nmColId: ROUTE_NM_COL_ID, nmField: "routeNm", nmHeader: "FP_ROUTE_NM",
      hideKey: ROUTE_NM_HIDE_KEY, width: 150
    }),

    // leaf(품목) — 그룹이 아니라 실제 행의 값.
    {
      headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", width: 150, pinned: "left", lockPinned: true,
      type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter,
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), reduxKey: ITEM_NM_HIDE_KEY, detailColumns: [ITEM_NM_COL_ID]
      }
    },
    {
      headerName: "FP_ITEM_NM", field: "itemNm", colId: ITEM_NM_COL_ID, width: 150, pinned: "left", lockPinned: true,
      hide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter
    },
    { headerName: "SUM", field: "sum", colId: "sum", width: 120, pinned: "left", lockPinned: true, type: ["numberColumn", "rightAligned"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // old columnProperty.styleCallback — 늦은(lateYn) 버킷 셀은 배경을 강조한다(companion 배열
  // pivotlateYnData, old setCrossTabGridDataWithBucketAndMultiPvtCol의 멀티 피벗 컬럼 대응).
  const lateCellStyle = (params) => (params.data?.[`${params.colDef.field}_lateYn`] ? { backgroundColor: "#FEC3C6" } : null);

  // 요일 코드 번역+토/일 강조색은 common/grid.js buildDayBucketPivotColumnDefs가 처리한다
  // (§A-6d/§A-6f). lateCellStyle은 이 화면 고유 강조라 childExtraProps로 얹는다.
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t, {
    childExtraProps: () => ({ cellStyle: lateCellStyle })
  });

  // old setCrossTabGridDataWithBucketAndMultiPvtCol — pivotData[i]를 crosstabHeader[i] 필드로,
  // pivotlateYnData[i]를 그 companion(`<header>_lateYn`)으로 함께 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      stageCd: row.stageCd, stageNm: row.stageNm,
      resourceCd: row.resourceCd, resourceNm: row.resourceNm, resourceUtil: row.resourceUtil,
      routeCd: row.routeCd, routeNm: row.routeNm,
      itemCd: row.itemCd, itemNm: row.itemNm, itemClassCd: row.itemClassCd,
      sum: row.sum
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
      flat[`${header}_lateYn`] = row.pivotlateYnData?.[index];
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      stageCdNm: stageCdNmRef.current,
      resourceCdNm: resourceCdNmRef.current,
      routeCdNm: routeCdNmRef.current,
      itemCdNm: itemCdNmRef.current,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — PlanResult.js와 동일한 슬롯 재활용 원칙(공장→originLoc, 스테이지→
  // destLoc, 자원→customer, 공정→item, 품목→workCenter — 5단 계층을 5개 슬롯에 순서대로 싣는다).
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      destLocCd: target.stageCd ?? null, destLocCdHeader: t("FP_STAGE_CD"),
      destLocNm: target.stageNm ?? null, destLocNmHeader: t("FP_STAGE_NM"),
      customerCd: target.resourceCd ?? null, customerCdHeader: t("FP_RESOURCE_CD"),
      customerNm: target.resourceNm ?? null, customerNmHeader: t("FP_RESOURCE_NM"),
      itemCd: target.routeCd ?? null, itemCdHeader: t("FP_ROUTE_CD"),
      itemNm: target.routeNm ?? null, itemNmHeader: t("FP_ROUTE_NM"),
      workCenterCd: target.itemCd ?? null, workCenterCdHeader: t("FP_ITEM_CD"),
      workCenterNm: target.itemNm ?? null, workCenterNmHeader: t("FP_ITEM_NM")
    });
  };

  // 자원 가동 지표 5종(듀얼 y축: 시간류 bar 좌축, 비율류 line 우축) — old fnSetResProdChartData 대응.
  const resourcePivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries: RESOURCE_INDICATOR_ORDER.map(indicator => ({
      field: indicator, text: indicator, type: RESOURCE_INDICATOR_TYPES[indicator],
      groupName: RESOURCE_INDICATOR_IS_RATE.has(indicator) ? "%" : `${t("TIME")}(${t("FP_MINUTES")})`,
      typeOptions: RESOURCE_INDICATOR_TYPES[indicator] === "bar"
        ? { stackId: "resProdPlanRes", fill: RESOURCE_INDICATOR_COLORS[indicator], name: t(indicator) }
        : { stroke: RESOURCE_INDICATOR_COLORS[indicator], name: t(indicator) }
    })),
    include: chartLabels,
    // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
    // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
    xAxisSortMap: buildXAxisSortMap(chartLabels)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [chartLabels, t]);

  // 품목 수급 지표 3종(단일 y축) — old fnSetItemProdChartData 대응.
  const itemPivotConfig = useMemo(() => ({
    groupKey: "analysisIndicator",
    chartSeries: ITEM_INDICATOR_ORDER.map(indicator => ({
      field: indicator, text: indicator, type: ITEM_INDICATOR_TYPES[indicator], groupName: t("QTY"),
      typeOptions: ITEM_INDICATOR_TYPES[indicator] === "bar"
        ? { stackId: "resProdPlanItem", fill: ITEM_INDICATOR_COLORS[indicator], name: t(indicator) }
        : { stroke: ITEM_INDICATOR_COLORS[indicator], name: t(indicator) }
    })),
    include: chartLabels,
    // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
    // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
    xAxisSortMap: buildXAxisSortMap(chartLabels)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [chartLabels, t]);

  // 클릭 기준으로 차트를 다시 조회한다. mode에 따라 엔드포인트/지표 세트가 통째로 바뀐다
  // (old updateChartFromGrid의 fieldName 분기 — 자원 칸=res-chart, 그 외=item-chart).
  const loadChart = (mode, target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = {
      mode,
      plantCd: target.plantCd ?? "", stageCd: target.stageCd ?? "", resourceCd: target.resourceCd ?? "",
      routeCd: target.routeCd ?? "", itemCd: target.itemCd ?? ""
    };
    const prev = prevChartParamsRef.current;
    if (prev && Object.keys(nextParams).every(field => prev[field] === nextParams[field])) return;
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    // old loadResUtilChart는 stageCd를 보내지 않는다(plantCd+resourceCd만) — old 그대로 이식.
    const params = mode === CHART_MODE_RESOURCE
      ? { ...buildBaseParams(), plantCd: nextParams.plantCd, resourceCd: nextParams.resourceCd }
      : { ...buildBaseParams(), ...nextParams, useGrouping: true };
    delete params.mode;

    const fetcher = mode === CHART_MODE_RESOURCE
      ? fpResProdPlanService.getResProdPlanResChart
      : fpResProdPlanService.getResProdPlanItemChart;

    fetcher(params)
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = data ?? [];

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartLabels([]);
          return;
        }

        // series 구성 자체는 mode별로 고정(resourcePivotConfig/itemPivotConfig useMemo가 담당) —
        // 여기서는 어느 pivotConfig를 렌더할지 결정하는 chartMode만 갱신한다.
        setChartMode(mode);
        setChartLabels(chartHeader);
        setChartData(rows.map(r => {
          const point = { analysisIndicator: r.analysisIndicator };
          chartHeader.forEach((header, index) => { point[header] = r.pivotData?.[index] ?? 0; });
          return point;
        }));
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveResProdPlan = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd };

    gridRef.current?.api?.setGridOption("loading", true);

    fpResProdPlanService.getResProdPlans(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행 기준 "자원" 차트를 그리고, 그리드의 선택 셀도 그 행으로 옮긴다
        // (old loadData가 loadResUtilChart(data[0])만 부르고 loadItemPsiChart는 안 부름).
        pendingFocusRef.current = { rowIndex: 0, colId: RESOURCE_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart(CHART_MODE_RESOURCE, { plantCd: rows[0].plantCd, resourceCd: rows[0].resourceCd });
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);
  const STAGE_LEVEL_COL_IDS = new Set([STAGE_CD_COL_ID, STAGE_NM_COL_ID]);
  const RESOURCE_LEVEL_COL_IDS = new Set([RESOURCE_CD_COL_ID, RESOURCE_NM_COL_ID]);
  const ROUTE_LEVEL_COL_IDS = new Set([ROUTE_CD_COL_ID, ROUTE_NM_COL_ID]);

  // 클릭한 셀에서 (모드, 차트 대상)을 뽑는다. old updateChartFromGrid의 fieldName 분기를 트리
  // 표시 컬럼 colId 기준으로 옮긴 것 — 자원 칸만 res-chart, 나머지(공장/스테이지/공정/품목/피벗
  // 데이터 셀)는 전부 item-chart(클릭 레벨까지 누적).
  const resolveChartClick = (params) => {
    const node = params.node;
    if (!node) return null;

    let identity;
    if (node.group) {
      identity = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) identity[current.field] = current.key;
      }
      if (isEmpty(identity)) return null;
    } else {
      identity = node.data;
      if (!identity) return null;
    }

    const colId = params.column?.getColId() ?? "";
    if (RESOURCE_LEVEL_COL_IDS.has(colId)) {
      return { mode: CHART_MODE_RESOURCE, target: { plantCd: identity.plantCd, resourceCd: identity.resourceCd } };
    }
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { mode: CHART_MODE_ITEM, target: { plantCd: identity.plantCd, plantNm: identity.plantNm } };
    }
    if (STAGE_LEVEL_COL_IDS.has(colId)) {
      return {
        mode: CHART_MODE_ITEM,
        target: {
          plantCd: identity.plantCd, plantNm: identity.plantNm, stageCd: identity.stageCd, stageNm: identity.stageNm
        }
      };
    }
    if (ROUTE_LEVEL_COL_IDS.has(colId)) {
      return {
        mode: CHART_MODE_ITEM,
        target: {
          plantCd: identity.plantCd, plantNm: identity.plantNm, stageCd: identity.stageCd, stageNm: identity.stageNm,
          routeCd: identity.routeCd, routeNm: identity.routeNm
        }
      };
    }
    return { mode: CHART_MODE_ITEM, target: identity };
  };

  const onCellClicked = (params) => {
    const resolved = resolveChartClick(params);
    if (resolved) loadChart(resolved.mode, resolved.target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod) — PlanResult.js와 동일 원리.
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간/버킷)이 바뀌면 값이 준비되는 즉시 자동 조회한다(§A-9b). 자유 텍스트
  // (stageCdNm/resourceCdNm/routeCdNm/itemCdNm)는 SearchOptsGroupRetrieve 내부 수동 커밋으로만 조회.
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          // Report 화면 bucketCase 배정표(fp-report-screen-guide.md §A-3) — ResProdPlan은 배정표에
          // 명시된 WITHOUT_HOUR 예외 화면(백엔드도 HOUR 버킷이면 빈 데이터를 반환 — Bucket.isHourBucket()).
          bucketCase={BUCKET_CASE.WITHOUT_HOUR}
        />
        <SearchOptsGroupRetrieve
          fieldRefs={{
            stageCdNm: stageCdNmRef, resourceCdNm: resourceCdNmRef, routeCdNm: routeCdNmRef, itemCdNm: itemCdNmRef
          }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveResProdPlan}
        />
        {/* old RightButtonArea 범례 칩(과부하 자원/지연 주문 존재) — 색상 강조 의미를 알려주는
            정적 안내. MUI 시맨틱 color(warning/error) 대신 실제 그리드 강조색(resourceOverloadCellStyle
            의 "#F5AB00", lateCellStyle의 "#FEC3C6")과 동일한 색으로 맞춰서 칩 색만 보고도 그리드의
            어떤 강조와 짝인지 바로 알 수 있게 한다. 이 그룹만 라벨이 없어서 형제 검색 필드들
            (라벨+입력 2단)과 높이 기준이 다르므로 height:100%+alignItems:center로 입력창들과 수직
            중앙을 맞춘다. ml은 다른 검색 그룹들의 간격 관례(ml:1.5)를 그대로 따른다. */}
        <Box sx={{ display: "flex", height: "100%", alignItems: "center", gap: 1, ml: 1.5 }}>
          <Chip size="small" label={t("RES_OVER_LOAD_BELOW")} sx={{ backgroundColor: "#F5AB00", color: "#000" }} />
          <Chip size="small" label={t("LATE_ORDER_EXIST")} sx={{ backgroundColor: "#FEC3C6", color: "#000" }} />
        </Box>
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-res-prod-plan-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* TODO(MIG-T2): [resprodplan/ResProdPlan] 차트 장식성 인터랙션 2종 미이관.
                old: ChartLegendPlugin(클릭 시 시리즈 숨김+취소선), ChartAxisTitlePlugin의 동적 y축 최대값 계산(chartMaxValue)
                사유: recharts/PivotChartComposed에 대응 훅이 없고, 1차 목표(조회·표시)에 불필요 — 가이드 §A-7에서 Tier2 확정 */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <PivotChartComposed
              pivotConfig={chartMode === CHART_MODE_RESOURCE ? resourcePivotConfig : itemPivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ResProdPlan);
